import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import agent from "../../app/api/agent";
import { fieldChecked, fieldValue, useModalOpen } from "../../app/components/polaris";
import {
    ShopifyMetafieldAttributeOption,
    ShopifyMetafieldAttributeOwnerType,
    ShopifyMetafieldAttributeUpsert,
    ShopifyMetafieldDefinitionOption,
} from "../../app/models/ShopifyMetafieldAttribute";
import type { EditorStatus } from "./editorStatus";

export type MetafieldAttributesEditorStatus = EditorStatus;

type MetafieldAttributesSettingsProps = {
    onStatusChange?: (status: MetafieldAttributesEditorStatus) => void;
};

type DraftAttribute = Omit<ShopifyMetafieldAttributeOption, "id"> & {
    id?: number | null;
};

type AttributeForm = {
    id?: number | null;
    ownerType: ShopifyMetafieldAttributeOwnerType;
    namespace: string;
    key: string;
    name: string;
    type: string;
    shopifyDefinitionId: string;
    description: string;
    isEnabled: boolean;
    displayOrder: string;
};

type AttributeEditorMode = "add" | "edit";

const emptyForm: AttributeForm = {
    id: null,
    ownerType: "ProductVariant",
    namespace: "",
    key: "",
    name: "",
    type: "single_line_text_field",
    shopifyDefinitionId: "",
    description: "",
    isEnabled: true,
    displayOrder: "0",
};

const ownerTypeOptions = [
    { label: "Variant metafield", value: "ProductVariant" },
    { label: "Product metafield", value: "Product" },
];

const ownerTypeLabel = (ownerType: ShopifyMetafieldAttributeOwnerType) =>
    ownerType === "ProductVariant" ? "Variant metafield" : "Product metafield";

const pathKey = (ownerType: ShopifyMetafieldAttributeOwnerType, namespace: string, key: string) =>
    `${ownerType}|${namespace.trim().toLowerCase()}|${key.trim().toLowerCase()}`;

const attributeIdentity = (attribute: DraftAttribute) =>
    attribute.id ? `id:${attribute.id}` : `path:${pathKey(attribute.ownerType, attribute.namespace, attribute.key)}`;

const formatDateTime = (value?: string | null) => {
    if (!value) return "Not synced";

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "Unknown" : date.toLocaleString();
};

const cloneAttributes = (attributes: DraftAttribute[]): DraftAttribute[] =>
    attributes.map((attribute) => ({
        ...attribute,
        values: attribute.values ? [...attribute.values] : [],
    }));

const normalizeAttributeSnapshot = (attribute: DraftAttribute) => ({
    id: attribute.id ?? null,
    ownerType: attribute.ownerType,
    namespace: attribute.namespace.trim(),
    key: attribute.key.trim(),
    name: attribute.name.trim(),
    type: attribute.type?.trim() || "single_line_text_field",
    shopifyDefinitionId: attribute.shopifyDefinitionId || "",
    description: attribute.description || "",
    isEnabled: !!attribute.isEnabled,
    displayOrder: Number(attribute.displayOrder) || 0,
});

const attributeSnapshot = (attributes: DraftAttribute[]) => JSON.stringify(
    attributes
        .map(normalizeAttributeSnapshot)
        .sort((left, right) =>
            (left.id ?? 0) - (right.id ?? 0)
            || left.ownerType.localeCompare(right.ownerType)
            || left.namespace.localeCompare(right.namespace)
            || left.key.localeCompare(right.key)
        )
);

const attributeToForm = (attribute: DraftAttribute): AttributeForm => ({
    id: attribute.id,
    ownerType: attribute.ownerType,
    namespace: attribute.namespace,
    key: attribute.key,
    name: attribute.name,
    type: attribute.type || "single_line_text_field",
    shopifyDefinitionId: attribute.shopifyDefinitionId || "",
    description: attribute.description || "",
    isEnabled: attribute.isEnabled,
    displayOrder: String(attribute.displayOrder ?? 0),
});

const formToPayload = (form: AttributeForm): ShopifyMetafieldAttributeUpsert => ({
    id: form.id,
    ownerType: form.ownerType,
    namespace: form.namespace.trim(),
    key: form.key.trim(),
    name: form.name.trim() || `${form.namespace.trim()}.${form.key.trim()}`,
    type: form.type.trim() || "single_line_text_field",
    shopifyDefinitionId: form.shopifyDefinitionId.trim(),
    description: form.description.trim(),
    isEnabled: form.isEnabled,
    displayOrder: Number.parseInt(form.displayOrder, 10) || 0,
});

const attributeToPayload = (attribute: DraftAttribute): ShopifyMetafieldAttributeUpsert => ({
    id: attribute.id,
    ownerType: attribute.ownerType,
    namespace: attribute.namespace.trim(),
    key: attribute.key.trim(),
    name: attribute.name.trim() || `${attribute.namespace.trim()}.${attribute.key.trim()}`,
    type: attribute.type?.trim() || "single_line_text_field",
    shopifyDefinitionId: attribute.shopifyDefinitionId || "",
    description: attribute.description || "",
    isEnabled: attribute.isEnabled,
    displayOrder: Number(attribute.displayOrder) || 0,
});

const payloadToDraftAttribute = (
    payload: ShopifyMetafieldAttributeUpsert,
    existingAttribute?: DraftAttribute
): DraftAttribute => ({
    id: payload.id ?? existingAttribute?.id ?? null,
    ownerType: payload.ownerType,
    namespace: payload.namespace,
    key: payload.key,
    name: payload.name,
    type: payload.type || "single_line_text_field",
    shopifyDefinitionId: payload.shopifyDefinitionId || "",
    description: payload.description || "",
    isEnabled: payload.isEnabled,
    displayOrder: payload.displayOrder,
    lastIndexedAt: existingAttribute?.lastIndexedAt,
    valueCount: existingAttribute?.valueCount ?? 0,
    values: existingAttribute?.values ?? [],
});

const definitionToDraftAttribute = (
    definition: ShopifyMetafieldDefinitionOption,
    existingAttribute: DraftAttribute | undefined,
    displayOrder: number
): DraftAttribute => ({
    id: existingAttribute?.id ?? null,
    ownerType: definition.ownerType,
    namespace: definition.namespace,
    key: definition.key,
    name: definition.name || `${definition.namespace}.${definition.key}`,
    type: definition.type || "single_line_text_field",
    shopifyDefinitionId: definition.shopifyDefinitionId || "",
    description: definition.description || "",
    isEnabled: true,
    displayOrder: existingAttribute?.displayOrder ?? displayOrder,
    lastIndexedAt: existingAttribute?.lastIndexedAt,
    valueCount: existingAttribute?.valueCount ?? 0,
    values: existingAttribute?.values ?? [],
});

const upsertDraftAttribute = (attributes: DraftAttribute[], nextAttribute: DraftAttribute) => {
    let replaced = false;
    const nextPath = pathKey(nextAttribute.ownerType, nextAttribute.namespace, nextAttribute.key);

    const nextAttributes = attributes.map((attribute) => {
        const sameId = !!nextAttribute.id && attribute.id === nextAttribute.id;
        const sameNewPath = !nextAttribute.id && pathKey(attribute.ownerType, attribute.namespace, attribute.key) === nextPath;

        if (!sameId && !sameNewPath) return attribute;

        replaced = true;
        return {
            ...attribute,
            ...nextAttribute,
            values: nextAttribute.values ?? attribute.values ?? [],
        };
    });

    if (!replaced) {
        nextAttributes.push(nextAttribute);
    }

    return nextAttributes.sort((left, right) =>
        (left.displayOrder ?? 0) - (right.displayOrder ?? 0)
        || (left.name || "").localeCompare(right.name || "")
    );
};

const MetafieldAttributesSettings: React.FC<MetafieldAttributesSettingsProps> = ({ onStatusChange }) => {
    const [attributes, setAttributes] = useState<DraftAttribute[]>([]);
    const [baselineAttributes, setBaselineAttributes] = useState<DraftAttribute[]>([]);
    const [definitions, setDefinitions] = useState<ShopifyMetafieldDefinitionOption[]>([]);
    const [form, setForm] = useState<AttributeForm>(emptyForm);
    const [selectedDefinitionKey, setSelectedDefinitionKey] = useState("");
    const [attributeEditorOpen, setAttributeEditorOpen] = useState(false);
    const [attributeEditorMode, setAttributeEditorMode] = useState<AttributeEditorMode>("add");
    const [manualOpen, setManualOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const [definitionError, setDefinitionError] = useState<string | undefined>(undefined);
    const [editingIdentity, setEditingIdentity] = useState<string>();
    const [loadFailed, setLoadFailed] = useState(false);
    const returnFocusRef = useRef<HTMLElement | null>(null);

    const loadData = useCallback(async () => {
        setLoading(true);

        try {
            let definitionsFailed = false;
            const definitionsRequest = agent.ShopifyMetafieldAttributes.definitions().catch(() => {
                definitionsFailed = true;
                return [] as ShopifyMetafieldDefinitionOption[];
            });

            const [loadedAttributes, loadedDefinitions] = await Promise.all([
                agent.ShopifyMetafieldAttributes.list({ enabledOnly: false, includeValues: false }),
                definitionsRequest,
            ]);

            const nextAttributes = cloneAttributes(loadedAttributes);
            setAttributes(nextAttributes);
            setBaselineAttributes(cloneAttributes(nextAttributes));
            setDefinitions(loadedDefinitions);
            setDefinitionError(definitionsFailed ? "Existing Shopify metafields could not be loaded. Use manual linking if needed." : undefined);
            setError(undefined);
            setLoadFailed(false);
            return true;
        } catch {
            setLoadFailed(true);
            setError("Attributes could not be loaded.");
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const hasUnsavedChanges = useMemo(
        () => attributeSnapshot(attributes) !== attributeSnapshot(baselineAttributes),
        [attributes, baselineAttributes]
    );

    const removedAttributes = useMemo(
        () => baselineAttributes.filter(baseline => !attributes.some(attribute => attributeIdentity(attribute) === attributeIdentity(baseline))),
        [attributes, baselineAttributes]
    );

    const enabledPathKeys = useMemo(
        () => new Set(attributes.filter((attribute) => attribute.isEnabled).map((attribute) => pathKey(attribute.ownerType, attribute.namespace, attribute.key))),
        [attributes]
    );

    const attributeByPath = useMemo(() => {
        const map = new Map<string, DraftAttribute>();
        // Reconnecting before Save restores the persisted identity and cached values.
        [...removedAttributes, ...attributes].forEach((attribute) => {
            map.set(pathKey(attribute.ownerType, attribute.namespace, attribute.key), attribute);
        });
        return map;
    }, [attributes, removedAttributes]);

    const baselineByIdentity = useMemo(() => {
        const map = new Map<string, string>();
        baselineAttributes.forEach((attribute) => {
            map.set(attributeIdentity(attribute), JSON.stringify(normalizeAttributeSnapshot(attribute)));
        });
        return map;
    }, [baselineAttributes]);

    const definitionGroups = useMemo(
        () => ownerTypeOptions.map((ownerType) => ({
                title: `${ownerType.label}s`,
                options: definitions
                    .filter((definition) => definition.ownerType === ownerType.value)
                    .map((definition) => {
                        const key = pathKey(definition.ownerType, definition.namespace, definition.key);
                        const enabled = enabledPathKeys.has(key);
                        return {
                            label: `${definition.name || `${definition.namespace}.${definition.key}`}${enabled ? " (enabled)" : ""}`,
                            value: key,
                        };
                    }),
            })).filter((group) => group.options.length > 0),
        [definitions, enabledPathKeys]
    );

    const selectedDefinition = definitions.find(
        (definition) => pathKey(definition.ownerType, definition.namespace, definition.key) === selectedDefinitionKey
    );
    const selectedDefinitionExistingAttribute = selectedDefinition ? attributeByPath.get(selectedDefinitionKey) : undefined;
    const selectedDefinitionAlreadyEnabled = enabledPathKeys.has(selectedDefinitionKey);
    const enabledAttributeCount = attributes.filter((attribute) => attribute.isEnabled).length;
    const hasAttributes = attributes.length > 0;
    const nextDisplayOrder = Math.max(0, ...attributes.map((attribute) => attribute.displayOrder ?? 0)) + 10;

    const closeEditor = useCallback(() => {
        setAttributeEditorOpen(false);
        setAttributeEditorMode("add");
        setSelectedDefinitionKey("");
        setManualOpen(false);
        setForm(emptyForm);
        setError(undefined);
        setEditingIdentity(undefined);
        requestAnimationFrame(() => {
            const target = returnFocusRef.current;
            if (target?.isConnected) target.focus();
            else document.getElementById("connect-product-attribute")?.focus();
        });
    }, []);

    const openAddAttributeEditor = useCallback(() => {
        returnFocusRef.current = document.activeElement as HTMLElement | null;
        setEditingIdentity(undefined);
        setAttributeEditorMode("add");
        setForm({ ...emptyForm, displayOrder: String(nextDisplayOrder) });
        setSelectedDefinitionKey("");
        setManualOpen(false);
        setAttributeEditorOpen(true);
        setError(undefined);
    }, [nextDisplayOrder]);

    const openEditAttributeEditor = useCallback((attribute: DraftAttribute) => {
        returnFocusRef.current = document.activeElement as HTMLElement | null;
        setEditingIdentity(attributeIdentity(attribute));
        setAttributeEditorMode("edit");
        setForm(attributeToForm(attribute));
        setSelectedDefinitionKey("");
        setManualOpen(true);
        setAttributeEditorOpen(true);
        setError(undefined);
    }, []);

    const toggleAttribute = useCallback((attribute: DraftAttribute, isEnabled: boolean) => {
        setAttributes((current) => current.map((currentAttribute) =>
            attributeIdentity(currentAttribute) === attributeIdentity(attribute)
                ? { ...currentAttribute, isEnabled }
                : currentAttribute
        ));
    }, []);

    const disconnectAttribute = useCallback((attribute: DraftAttribute) => {
        setAttributes(current => current.filter(item => attributeIdentity(item) !== attributeIdentity(attribute)));
        setError(undefined);
        requestAnimationFrame(() => document.getElementById("connect-product-attribute")?.focus());
    }, []);

    const addSelectedDefinitionToDraft = useCallback(() => {
        if (!selectedDefinition) {
            setError("Select a Shopify metafield first.");
            return;
        }

        if (selectedDefinitionAlreadyEnabled) {
            setError("That metafield is already connected.");
            return;
        }

        const nextAttribute = definitionToDraftAttribute(selectedDefinition, selectedDefinitionExistingAttribute, nextDisplayOrder);
        setAttributes((current) => upsertDraftAttribute(current, nextAttribute));
        closeEditor();
    }, [
        nextDisplayOrder,
        closeEditor,
        selectedDefinition,
        selectedDefinitionAlreadyEnabled,
        selectedDefinitionExistingAttribute,
    ]);

    const saveManualAttributeToDraft = useCallback(() => {
        const payload = formToPayload(form);
        if (!payload.namespace || !payload.key) {
            setError("Namespace and key are required.");
            return;
        }

        const duplicate = attributes.find((attribute) =>
            attributeIdentity(attribute) !== editingIdentity
            && pathKey(attribute.ownerType, attribute.namespace, attribute.key) === pathKey(payload.ownerType, payload.namespace, payload.key)
        );

        if (duplicate) {
            setError("That metafield is already linked as an attribute.");
            return;
        }

        const existingAttribute = editingIdentity
            ? attributes.find((attribute) => attributeIdentity(attribute) === editingIdentity)
            : attributeByPath.get(pathKey(payload.ownerType, payload.namespace, payload.key));

        setAttributes((current) => upsertDraftAttribute(
            editingIdentity ? current.filter(attribute => attributeIdentity(attribute) !== editingIdentity) : current,
            payloadToDraftAttribute(payload, existingAttribute)
        ));
        closeEditor();
    }, [attributeByPath, attributes, editingIdentity, form, closeEditor]);

    const applyEditorToDraft = useCallback(() => {
        if (attributeEditorMode === "edit" || manualOpen) {
            saveManualAttributeToDraft();
            return;
        }

        addSelectedDefinitionToDraft();
    }, [addSelectedDefinitionToDraft, attributeEditorMode, manualOpen, saveManualAttributeToDraft]);

    const discardChanges = useCallback(() => {
        setAttributes(cloneAttributes(baselineAttributes));
        closeEditor();
        setError(undefined);
    }, [baselineAttributes, closeEditor]);

    const saveChanges = useCallback(async () => {
        if (!hasUnsavedChanges) return;

        const changedAttributes = attributes.filter((attribute) =>
            baselineByIdentity.get(attributeIdentity(attribute)) !== JSON.stringify(normalizeAttributeSnapshot(attribute))
        );

        setSaving(true);
        setError(undefined);

        try {
            // Persist removals first so dependency errors leave metadata edits untouched.
            for (const attribute of removedAttributes) {
                if (attribute.id) await agent.ShopifyMetafieldAttributes.delete(attribute.id);
                setBaselineAttributes(current => current.filter(item => attributeIdentity(item) !== attributeIdentity(attribute)));
            }
            for (const attribute of changedAttributes) {
                const saved = await agent.ShopifyMetafieldAttributes.upsert(attributeToPayload(attribute));
                const persistedAttribute = { ...attribute, id: saved.id };
                setAttributes(current => current.map(item => attributeIdentity(item) === attributeIdentity(attribute) ? persistedAttribute : item));
                setBaselineAttributes(current => upsertDraftAttribute(current, persistedAttribute));
            }

            if (await loadData()) window.shopify?.toast?.show?.("Product attributes saved");
        } catch (saveError) {
            const response = (saveError as { response?: { status?: number; data?: { message?: string } } })?.response;
            setError(response?.status === 409 && typeof response.data?.message === "string"
                ? response.data.message
                : "Product attributes could not be saved.");
        } finally {
            setSaving(false);
        }
    }, [attributes, baselineByIdentity, hasUnsavedChanges, loadData, removedAttributes]);

    useEffect(() => {
        onStatusChange?.({
            hasChanges: hasUnsavedChanges,
            loading,
            saving,
            save: saveChanges,
            discard: discardChanges,
        });
    }, [discardChanges, hasUnsavedChanges, loading, onStatusChange, saveChanges, saving]);

    const editorPrimaryDisabled = saving
        || (attributeEditorMode === "add" && !manualOpen && (!selectedDefinition || selectedDefinitionAlreadyEnabled))
        || ((attributeEditorMode === "edit" || manualOpen) && (!form.namespace.trim() || !form.key.trim()));

    const editorRef = useModalOpen(attributeEditorOpen, () => { if (!saving) closeEditor(); });
    const editingAttribute = attributes.find(attribute => attributeIdentity(attribute) === editingIdentity);
    const updateForm = (changes: Partial<AttributeForm>) => setForm(current => ({ ...current, ...changes }));
    const connectionFields = <>
        <s-select label="Owner" value={form.ownerType}
            onChange={event => updateForm({ ownerType: fieldValue(event) as ShopifyMetafieldAttributeOwnerType })}>
            {ownerTypeOptions.map(option => <s-option key={option.value} value={option.value}>{option.label}</s-option>)}
        </s-select>
        <s-text-field label="Namespace" value={form.namespace} autocomplete="off" onInput={event => updateForm({ namespace: fieldValue(event) })} />
        <s-text-field label="Key" value={form.key} autocomplete="off" onInput={event => updateForm({ key: fieldValue(event) })} />
        <s-number-field label="Display order" value={form.displayOrder} onInput={event => updateForm({ displayOrder: fieldValue(event) })} />
    </>;

    if (loading && attributes.length === 0) {
        return (
            <s-section>
                <s-stack alignItems="center" padding="base">
                    <s-spinner accessibilityLabel="Loading product attributes" />
                </s-stack>
            </s-section>
        );
    }

    return (
        <>
            {error && !attributeEditorOpen && <s-banner tone="critical">{error}</s-banner>}
            {removedAttributes.length > 0 && <s-banner tone="info">
                {removedAttributes.length === 1 ? "1 attribute will be disconnected" : `${removedAttributes.length} attributes will be disconnected`} when you save.
            </s-banner>}
            <s-section heading="Product attributes">
                <s-stack gap="base">
                {loadFailed ? <s-stack direction="inline"><s-button onClick={loadData} loading={loading}>Retry loading attributes</s-button></s-stack> : <>
                <s-stack direction="inline">
                    <s-button id="connect-product-attribute" icon="plus" onClick={openAddAttributeEditor}
                        disabled={loading || saving || attributeEditorOpen}>Connect metafield</s-button>
                </s-stack>
                {!hasAttributes ? <s-text color="subdued">No product attributes connected</s-text> : (
                    <s-table>
                        <s-table-header-row>
                            <s-table-header listSlot="primary">Attribute</s-table-header>
                            <s-table-header>Cached values</s-table-header>
                            <s-table-header>{`Use in app (${enabledAttributeCount} on)`}</s-table-header>
                            <s-table-header><s-text accessibilityVisibility="exclusive">Actions</s-text></s-table-header>
                        </s-table-header-row>
                        <s-table-body>
                            {attributes.map(attribute => {
                                const name = attribute.name || `${attribute.namespace}.${attribute.key}`;
                                const busy = saving || loading || attributeEditorOpen;
                                return (
                                    <s-table-row key={attributeIdentity(attribute)}>
                                        <s-table-cell>
                                            <s-stack gap="small-100">
                                                <s-text fontWeight="semibold">{name}</s-text>
                                                <s-text color="subdued">{`${attribute.ownerType === "ProductVariant" ? "Variant" : "Product"} · ${attribute.namespace}.${attribute.key}`}</s-text>
                                            </s-stack>
                                        </s-table-cell>
                                        <s-table-cell>
                                            {attribute.valueCount > 0
                                                ? `${attribute.valueCount} ${attribute.valueCount === 1 ? "value" : "values"}`
                                                : attribute.lastIndexedAt ? "No values" : "Not synced"}
                                        </s-table-cell>
                                        <s-table-cell>
                                            <s-switch label={`Use ${name} in app`} labelAccessibilityVisibility="exclusive"
                                                checked={attribute.isEnabled} disabled={busy}
                                                onChange={event => toggleAttribute(attribute, fieldChecked(event))} />
                                        </s-table-cell>
                                        <s-table-cell>
                                            <s-button-group>
                                                <s-button slot="secondary-actions" variant="tertiary" accessibilityLabel={`Edit ${name}`}
                                                    disabled={busy} onClick={() => openEditAttributeEditor(attribute)}>Edit</s-button>
                                                <s-button slot="secondary-actions" variant="tertiary" tone="critical" accessibilityLabel={`Disconnect ${name}`}
                                                    disabled={busy} onClick={() => disconnectAttribute(attribute)}>Disconnect</s-button>
                                            </s-button-group>
                                        </s-table-cell>
                                    </s-table-row>
                                );
                            })}
                        </s-table-body>
                    </s-table>
                )}
                </>}
                </s-stack>
            </s-section>

            <s-modal ref={editorRef} id="pf-attribute-editor" heading={attributeEditorMode === "edit" ? `Edit ${editingAttribute?.name || "attribute"}` : "Connect metafield"}>
                {/* s-modal keeps its children mounted; render the form only while open. */}
                {attributeEditorOpen && <s-stack gap="base">
                    {error && <s-banner tone="critical">{error}</s-banner>}
                    {definitionError && attributeEditorMode === "add" && <s-banner tone="warning">{definitionError}</s-banner>}
                    {attributeEditorMode === "add" && !manualOpen && <s-select label="Existing Shopify metafield" value={selectedDefinitionKey}
                        disabled={definitions.length === 0 || saving} onChange={event => setSelectedDefinitionKey(fieldValue(event))}>
                        <s-option value="">{definitions.length === 0 ? "No Shopify metafields found" : "Select a Shopify metafield"}</s-option>
                        {definitionGroups.map(group => <s-option-group key={group.title} label={group.title}>
                            {group.options.map(option => <s-option key={option.value} value={option.value}>{option.label}</s-option>)}
                        </s-option-group>)}
                    </s-select>}
                    {attributeEditorMode === "add" && selectedDefinition && !manualOpen && <s-text color="subdued">
                        {`${ownerTypeLabel(selectedDefinition.ownerType)} · ${selectedDefinition.namespace}.${selectedDefinition.key}`}
                    </s-text>}
                    {attributeEditorMode === "add" && <s-stack direction="inline">
                        <s-button variant="tertiary" disabled={saving} onClick={() => setManualOpen(open => !open)}>
                            {manualOpen ? "Use metafield picker" : "Enter manually"}
                        </s-button>
                    </s-stack>}
                    {editingAttribute && <s-text color="subdued">
                        {`${ownerTypeLabel(editingAttribute.ownerType)} · ${editingAttribute.valueCount} cached ${editingAttribute.valueCount === 1 ? "value" : "values"} · Last synced ${formatDateTime(editingAttribute.lastIndexedAt)}`}
                    </s-text>}
                    {(manualOpen || attributeEditorMode === "edit") && <>
                        {attributeEditorMode === "add" && connectionFields}
                        <s-text-field label="Display name" value={form.name} autocomplete="off" onInput={event => updateForm({ name: fieldValue(event) })} />
                        <s-text-area label="Description" value={form.description} rows={3} onInput={event => updateForm({ description: fieldValue(event) })} />
                        <s-checkbox label="Use in app" checked={form.isEnabled} disabled={saving}
                            onChange={event => updateForm({ isEnabled: fieldChecked(event) })} />
                        {attributeEditorMode === "edit" && connectionFields}
                    </>}
                </s-stack>}
                <s-button slot="primary-action" variant="primary" disabled={editorPrimaryDisabled} onClick={applyEditorToDraft}>
                    {selectedDefinitionAlreadyEnabled && attributeEditorMode === "add" && !manualOpen ? "Already connected" : attributeEditorMode === "edit" ? "Done" : "Add"}
                </s-button>
                <s-button slot="secondary-actions" disabled={saving} onClick={closeEditor}>Cancel</s-button>
            </s-modal>
        </>
    );
};

export default MetafieldAttributesSettings;
